import { useCallback, useMemo, useState } from "react";
import {
  MAX_SEGMENT_CONDITIONS,
  type ConditionOperator,
  type SegmentConditionDraft,
  type SegmentRules,
} from "./CreateSegment.Constants";
import { analyzeSegmentPromptMock, type PromptClarification } from "./SegmentPrompt.Mocks";

interface FilledPrompt {
  text: string;
  count: number;
  previousRules: SegmentRules;
}

interface UseSegmentPromptOptions {
  rules: SegmentRules;
  onFill: (_drafts: SegmentConditionDraft[], _operators: ConditionOperator[]) => void;
  onRestore: (_rules: SegmentRules) => void;
}

export const useSegmentPrompt = ({ rules, onFill, onRestore }: UseSegmentPromptOptions) => {
  const [text, setText] = useState("");
  const [filledPrompt, setFilledPrompt] = useState<FilledPrompt | null>(null);

  const analysis = useMemo(() => analyzeSegmentPromptMock(text), [text]);
  const hasIssues = analysis.clarifications.length > 0 || analysis.issues.length > 0;
  const filledCount = filledPrompt?.text === text ? filledPrompt.count : null;
  const canFill = Boolean(text.trim()) && analysis.conditions.length > 0 && !hasIssues;

  const fill = useCallback(() => {
    if (!canFill) {
      return;
    }
    setFilledPrompt({
      text,
      count: Math.min(analysis.conditions.length, MAX_SEGMENT_CONDITIONS),
      previousRules: filledPrompt?.text === text ? filledPrompt.previousRules : rules,
    });
    onFill(analysis.conditions, analysis.operators);
  }, [canFill, filledPrompt, text, analysis, rules, onFill]);

  const undo = useCallback(() => {
    if (filledPrompt) {
      onRestore(filledPrompt.previousRules);
      setFilledPrompt(null);
    }
  }, [filledPrompt, onRestore]);

  const acceptSuggestion = useCallback(() => {
    setText(prev => `${prev}${analysis.suggestion}`);
  }, [analysis.suggestion]);

  const resolveClarification = useCallback(
    (clarification: PromptClarification, replacement: string) => {
      setText(prev => `${prev.slice(0, clarification.start)}${replacement}${prev.slice(clarification.end)}`);
    },
    []
  );

  return {
    text,
    setText,
    analysis,
    hasIssues,
    canFill,
    filledCount,
    fill,
    undo,
    acceptSuggestion,
    resolveClarification,
  };
};

export type SegmentPromptState = ReturnType<typeof useSegmentPrompt>;
